import { useState } from 'react';
import {
  confirmSalonFaceMatch,
  persistFacePending,
  persistFaceProfileSnapshot,
  readFaceCaptureBlob,
} from '../utils/faceIdentityApi';
import { resolveFacePortraitUrl } from '../utils/facePortrait';
import './ConfirmFaceMatchPicker.css';

function displayName(user) {
  return (
    user?.full_name ||
    user?.name ||
    user?.profile?.full_name ||
    'Guest'
  ).trim() || 'Guest';
}

function formatMobile(mobile) {
  const raw = String(mobile || '').trim();
  if (!raw) return '';
  if (raw.startsWith('+')) return raw;
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `+91 ${digits}`;
  if (digits.length > 10) return `+${digits}`;
  return raw;
}

function formatVisitDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch (_) {
    return String(iso);
  }
}

function candidateDetails(user, profile) {
  const hs = profile?.history_summary || user?.profile?.history_summary || {};
  const lastVisit = hs.last_visit || {};
  const visitedAt = lastVisit.visited_at || lastVisit.created_at || user?.last_visit_at;
  const loc = hs.last_visit_location || {};
  const locationLabel = [loc.area, loc.city, loc.state].filter(Boolean).join(', ');
  const summary = lastVisit.services_summary || {};
  const rawServices =
    summary.selected_services ||
    summary.service_names ||
    summary.services ||
    user?.last_selected_services ||
    [];
  const serviceNames = (Array.isArray(rawServices) ? rawServices : [])
    .map((s) => (typeof s === 'string' ? s : s?.name || s?.label || ''))
    .filter(Boolean)
    .slice(0, 3);
  const visitCount = Number(hs.visit_count ?? user?.visit_count ?? 0);
  const lastRef =
    user?.last_salon_reference ||
    lastVisit.salon_reference ||
    profile?.last_salon_reference ||
    '';
  return {
    visitedAt,
    locationLabel,
    serviceNames,
    visitCount,
    lastRef,
    gender: user?.gender || profile?.preferences?.gender || '',
  };
}

/**
 * Shown when recognize returns confirm_match (e.g. haircut / beard change).
 */
export default function ConfirmFaceMatchPicker({
  candidates = [],
  message,
  captureUrl,
  onConfirmed,
  onScanAgain,
  onUseMobile,
}) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  const pick = async (candidate) => {
    const userId = candidate?.user_id || candidate?.user?.id;
    if (!userId || busyId) return;
    setError('');
    setBusyId(userId);
    try {
      const blob = readFaceCaptureBlob();
      if (!blob) {
        throw new Error('Capture expired — please scan again.');
      }
      const data = await confirmSalonFaceMatch(userId, blob);
      if (!data?.user) {
        throw new Error(data?.message || 'Could not confirm profile');
      }
      const profile = data.profile || data.user.profile || null;
      const snapshot = {
        faceUserId: data.user.id,
        user: data.user,
        profile,
        confidence: data.confidence ?? candidate.confidence ?? null,
        mode: 'returning',
        captureUrl,
      };
      persistFaceProfileSnapshot(snapshot);
      persistFacePending(snapshot);
      await onConfirmed(snapshot, data);
    } catch (e) {
      setError(e?.message || 'Could not confirm — try again or scan again');
      setBusyId(null);
    }
  };

  return (
    <div className="fi-confirm-match">
      <h1 className="fi-confirm-match__title">Is this you?</h1>
      <p className="fi-confirm-match__lead">
        {message || 'We found familiar profiles. Select yours — we will notify the front desk.'}
      </p>
      {captureUrl ? (
        <div className="fi-confirm-match__capture" aria-hidden="true">
          <img src={captureUrl} alt="" />
          <span>Your scan</span>
        </div>
      ) : null}
      {error ? <p className="fi-confirm-match__error" role="alert">{error}</p> : null}
      <ul className="fi-confirm-match__list">
        {candidates.map((row) => {
          const user = row.user || {};
          const profile = user.profile || row.profile || null;
          const id = row.user_id || user.id;
          const name = displayName(user);
          const mobile = formatMobile(user.mobile || user.phone);
          const portrait = resolveFacePortraitUrl(user, profile, row);
          const meta = candidateDetails(user, profile);
          return (
            <li key={id}>
              <button
                type="button"
                className="fi-confirm-match__card"
                disabled={Boolean(busyId)}
                onClick={() => pick(row)}
              >
                <span className="fi-confirm-match__avatar">
                  {portrait ? (
                    <img src={portrait} alt="" className="fi-confirm-match__avatar-img" />
                  ) : (
                    name.charAt(0).toUpperCase()
                  )}
                </span>
                <span className="fi-confirm-match__body">
                  <strong>{name}</strong>
                  {mobile ? <span className="fi-confirm-match__mobile-line">{mobile}</span> : null}
                  {meta.lastRef ? <span>Last visit ref: {meta.lastRef}</span> : null}
                  {meta.visitedAt ? (
                    <span>Last visit: {formatVisitDate(meta.visitedAt)}</span>
                  ) : null}
                  {meta.locationLabel ? <span>{meta.locationLabel}</span> : null}
                  {meta.serviceNames.length ? (
                    <span>Previously: {meta.serviceNames.join(', ')}</span>
                  ) : null}
                  {meta.visitCount > 0 ? (
                    <span>{meta.visitCount} past visit{meta.visitCount === 1 ? '' : 's'}</span>
                  ) : null}
                  {row.confidence != null ? (
                    <span className="fi-confirm-match__conf">
                      Face match {Math.round(Number(row.confidence) * 100)}%
                    </span>
                  ) : null}
                </span>
                <span className="fi-confirm-match__cta">
                  {busyId === id ? 'Sending…' : "Yes, that's me"}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {onUseMobile ? (
        <button
          type="button"
          className="fi-confirm-match__scan-again fi-confirm-match__mobile"
          onClick={onUseMobile}
          disabled={Boolean(busyId)}
        >
          Enter mobile number (with country code)
        </button>
      ) : null}
      <button
        type="button"
        className="fi-confirm-match__scan-again"
        onClick={onScanAgain}
        disabled={Boolean(busyId)}
      >
        Not me — scan again
      </button>
    </div>
  );
}
